import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { MenuItem } from "primeng/api";
import { Idle, DEFAULT_INTERRUPTSOURCES } from "@ng-idle/core";
import { DialogService } from "primeng/dynamicdialog";
import { Keepalive } from "@ng-idle/keepalive";
import { LoginService } from "../../components/login/login.service";
import { AuthService } from "@auth0/auth0-angular";
import {
  SESSION_STORAGE_KEYS,
  SessionTimeoutService,
} from "../../services/session-timeout.service";

@Component({
  selector: 'app-home-layout',
  templateUrl: './home-layout.component.html',
  styleUrls: ['./home-layout.component.css'],
  providers: [DialogService],
})
export class HomeLayoutComponent implements OnInit, OnDestroy {
  items: MenuItem[];
  title = "imageonecontainer";

  idleState = "NOT_STARTED";
  countdown?: number = null;
  lastPing?: Date = null;
  showAutoLogout: Boolean = false;
  showSpinner: Boolean = false;
  countdownInterval: ReturnType<typeof setInterval> | null = null;

  private readonly storageListener = (event: StorageEvent) => this.onStorageEvent(event);
  /** Throttle warning-period activity so mousemove does not call reset() continuously. */
  private lastWarningDismissAt = 0;

  /**
   * Inactivity timeout uses @ng-idle/core:
   * - Production: 3600s idle, then 60s warning countdown, then logout.
   * - Dev/staging: 604800s (7 days) idle for easier testing.
   * Logout always runs through SessionTimeoutService.performLogout(), which POSTs
   * /logout to close the backend login report before Auth0 sign-out.
   */
  constructor(
    public loginService: LoginService,
    public authService: AuthService,
    private idle: Idle,
    private keepalive: Keepalive,
    private cd: ChangeDetectorRef,
    private sessionTimeout: SessionTimeoutService,
  ) {
    window?.localStorage?.setItem(SESSION_STORAGE_KEYS.TIMER_STARTED, 'false');

    idle.setIdle(this.sessionTimeout.idleSeconds);
    idle.setTimeout(this.sessionTimeout.countdownSeconds);
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleStart.subscribe(() => {
      this.idleState = "IDLE";
      idle.clearInterrupts();
      this.checkLogin();
    });

    idle.onIdleEnd.subscribe(() => {
      if (window?.localStorage?.getItem(SESSION_STORAGE_KEYS.BEARER_TOKEN)) {
        this.idleState = "NOT_IDLE";
        this.resetLocalOnly();
      } else {
        this.triggerLogout();
      }
    });

    // Countdown expiry is handled by the shared interval below; do not logout here
    // as well or the backend /logout endpoint may be called twice.
    idle.onTimeout.subscribe(() => {
      this.idleState = "TIMED_OUT";
      this.countdown = null;
    });

    idle.onTimeoutWarning.subscribe(() => {
      this.checkLogin();
      this.showAutoLogout = true;
      if (!this.sessionTimeout.isCountdownRunning()) {
        this.startCountdownInterval();
      }
    });

    // @ng-idle already listens for mouse/keyboard/scroll — use that for cross-tab
    // sync instead of duplicate document HostListeners (which fired on every mousemove).
    idle.onInterrupt.subscribe(() => {
      if (this.sessionTimeout.isLogoutInProgress() || this.showAutoLogout) {
        return;
      }
      this.sessionTimeout.recordActivity();
    });

    keepalive.onPing.subscribe(() => {
      this.lastPing = new Date();
    });
  }

  /** Exposed to template for the auto-logout dialog countdown display. */
  get getCountdownSeconds(): number | null {
    return this.sessionTimeout.getCountdownSeconds();
  }

  /**
   * When the tab closes during an active auto-logout countdown, attempt a
   * keepalive /logout so the backend login report is closed. Do not reset idle
   * here — that previously cancelled timeouts on refresh and inflated sessions.
   */
  @HostListener('window:pagehide')
  onPageHide(): void {
    if (this.sessionTimeout.isCountdownRunning() || this.showAutoLogout) {
      this.stopTracking();
      this.sessionTimeout.sendLogoutKeepalive();
    }
  }

  @HostListener('window:focus')
  onFocus(): void {
    this.handleCountdownExpiry();
  }

  /**
   * During the 60s warning @ng-idle interrupts are cleared, so handle activity here.
   * Throttled — without this, mousemove alone pegs CPU across multiple tabs.
   */
  @HostListener('document:mousemove')
  @HostListener('document:keydown')
  @HostListener('document:click')
  onWarningPeriodActivity(): void {
    if (!this.showAutoLogout || this.sessionTimeout.isLogoutInProgress()) {
      return;
    }

    const now = Date.now();
    if (now - this.lastWarningDismissAt < 1000) {
      return;
    }
    this.lastWarningDismissAt = now;

    this.sessionTimeout.recordActivity();
    this.resetLocalOnly();
  }

  ngOnInit(): void {
    window.addEventListener('storage', this.storageListener, false);

    if (this.loginService.isLoggedIn()) {
      this.resetLocalOnly();
      this.syncCountdownFromStorage();
    }

    this.loginService.userLoggedIn.subscribe(() => {
      this.resetLocalOnly();
      this.sessionTimeout.recordActivity();
    });

    this.loginService.userLoggedOut.subscribe(() => {
      this.stopTracking();
      window?.localStorage?.clear();
      window?.localStorage?.setItem(SESSION_STORAGE_KEYS.LOGGED_IN, 'false');
      window.location.reload();
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('storage', this.storageListener, false);
    this.stopTracking();
  }

  /**
   * Multi-tab storage sync:
   * - loggedIn: redirect when another tab logs in/out
   * - lastUserActivity: restart idle in other tabs WITHOUT writing localStorage again
   * - timerCloseTime/timerStarted: show shared countdown dialog in all tabs
   * - sessionLogout: stop tracking and redirect without leaving orphan sessions
   */
  private onStorageEvent(event: StorageEvent): void {
    if (event.key === SESSION_STORAGE_KEYS.LOGGED_IN) {
      if (window?.localStorage?.getItem(SESSION_STORAGE_KEYS.LOGGED_IN) === 'true') {
        window.location.reload();
      } else {
        this.stopTracking();
        window.location.href = '/login';
      }
      return;
    }

    if (event.key === SESSION_STORAGE_KEYS.LAST_ACTIVITY) {
      this.applyRemoteActivity();
      return;
    }

    if (
      event.key === SESSION_STORAGE_KEYS.TIMER_CLOSE_TIME ||
      event.key === SESSION_STORAGE_KEYS.TIMER_STARTED
    ) {
      this.syncCountdownFromStorage();
      return;
    }

    if (event.key === SESSION_STORAGE_KEYS.SESSION_LOGOUT) {
      this.stopTracking();
      if (window?.localStorage?.getItem(SESSION_STORAGE_KEYS.LOGGED_IN) === 'false') {
        window.location.href = '/login';
      }
    }
  }

  /**
   * Another tab saw user activity. Restart our idle timer locally only — never call
   * recordActivity() here or tabs ping-pong storage events and spike CPU.
   */
  private applyRemoteActivity(): void {
    if (this.sessionTimeout.isLogoutInProgress() || !this.loginService.isLoggedIn()) {
      return;
    }

    if (this.showAutoLogout || this.sessionTimeout.isCountdownRunning()) {
      this.resetLocalOnly();
      return;
    }

    this.idle.watch();
  }

  private syncCountdownFromStorage(): void {
    if (this.sessionTimeout.isCountdownRunning()) {
      this.showAutoLogout = true;
    } else {
      this.showAutoLogout = false;
      this.clearCountdownInterval();
    }
  }

  /** Only the tab that enters idle starts the interval; other tabs just show the dialog. */
  startCountdownInterval(): void {
    if (this.countdownInterval || this.sessionTimeout.isCountdownRunning()) {
      return;
    }

    this.sessionTimeout.startCountdown();

    this.countdownInterval = setInterval(() => {
      if (this.sessionTimeout.isCountdownExpired()) {
        this.triggerLogout();
      }
    }, 1000);
  }

  private handleCountdownExpiry(): void {
    if (this.showAutoLogout && this.sessionTimeout.isCountdownExpired()) {
      this.triggerLogout();
    }
  }

  checkLogin() {
    if (!this.loginService.isLoggedIn()) {
      this.stopTracking();
      window.location.href = "/login";
    }
  }

  /** User clicked "Stay Logged In" — extend locally and notify other tabs once. */
  reset() {
    this.resetLocalOnly();
    this.sessionTimeout.recordActivity();
  }

  /** Restarts idle/countdown UI in this tab only; does not broadcast to other tabs. */
  private resetLocalOnly(): void {
    if (this.sessionTimeout.isLogoutInProgress()) {
      return;
    }

    this.sessionTimeout.clearCountdown();
    this.clearCountdownInterval();
    this.idle.stop();
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idleState = "NOT_IDLE";
    this.countdown = null;
    this.lastPing = null;
    this.showAutoLogout = false;
    this.idle.watch();
  }

  /** Stops idle watching and the countdown interval without signing the user out. */
  stopTracking(): void {
    this.sessionTimeout.clearCountdown();
    this.clearCountdownInterval();
    this.idle.stop();
    this.showAutoLogout = false;
  }

  private clearCountdownInterval(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  /** Logout stops all tracking first, then closes the backend login report via /logout. */
  logout() {
    this.triggerLogout();
  }

  private triggerLogout(): void {
    if (this.sessionTimeout.isLogoutInProgress()) {
      return;
    }

    this.showSpinner = true;
    this.stopTracking();
    this.sessionTimeout.performLogout(this.loginService, this.authService);
  }
}
